import { normalizeLangKey } from '@src/utility/bcp47';

import { toPersistedKaraokeClip } from './toPersistedKaraokeClip';

import type { CaptionSetKind } from '../../dubbing/heardDub';
import type {
  DubRowRecord,
  KaraokeData,
  KaraokeLine,
} from '../../types/karaoke';
import type { CaptionLineBreak } from './CaptionLineBreak';

/**
 * PLAT-11905 — the caption track's two sets, in and out of the saved shape.
 *
 * Invariant: the saved shape's top-level `clips` and `syntheticBreaks` are
 * ALWAYS the source-timed set, whichever set the track shows, and the dub-timed
 * set travels beside it as `dubTimed`, a row record with no time in it. Every
 * save, snapshot and export goes through `composeCaptionSets`, so none of them
 * can write the shown dub captions where the source rows belong.
 */

/** What a caption track holds, as lines (the form every reader has). */
export interface CaptionSetState {
  liveKind: CaptionSetKind;
  /** The shown set's lines and break records. */
  liveLines: KaraokeLine[];
  liveBreaks: CaptionLineBreak[];
  /** The source-timed set while the dub-timed set is shown. */
  parkedSourceLines: KaraokeLine[] | null;
  parkedSourceBreaks: CaptionLineBreak[] | null;
  dubRecord: DubRowRecord | null;
}

export type ComposedCaptionSets = Pick<
  KaraokeData,
  'clips' | 'syntheticBreaks' | 'dubTimed'
>;

export function composeCaptionSets(
  state: CaptionSetState,
): ComposedCaptionSets {
  const showsDub = state.liveKind === 'dub';
  const sourceLines = showsDub
    ? (state.parkedSourceLines ?? [])
    : state.liveLines;
  const sourceBreaks = showsDub
    ? (state.parkedSourceBreaks ?? [])
    : state.liveBreaks;
  return {
    clips: sourceLines.map(toPersistedKaraokeClip),
    syntheticBreaks:
      sourceBreaks.length > 0 ? sourceBreaks.map(b => ({ ...b })) : undefined,
    ...(state.dubRecord ? { dubTimed: cloneDubRecord(state.dubRecord) } : {}),
  };
}

/**
 * The saved shape back into the track's two sets: the top-level rows and break
 * records ARE the source-timed set, as they are loaded today; this returns the
 * dub-timed record beside them. A record whose language is not the language
 * being loaded is ignored: the hydrate fallback can hand over another
 * language's blob.
 */
export function splitCaptionSets(
  data: Pick<KaraokeData, 'dubTimed'>,
  language: string | null | undefined,
): { dubRecord: DubRowRecord | null } {
  const record = data.dubTimed;
  const matches =
    !!record &&
    typeof record.language === 'string' &&
    !!language &&
    normalizeLangKey(record.language) === normalizeLangKey(language);
  return { dubRecord: matches && record ? cloneDubRecord(record) : null };
}

export function cloneDubRecord(record: DubRowRecord): DubRowRecord {
  return {
    language: record.language,
    segmentTexts: { ...record.segmentTexts },
    rows: record.rows.map(row => ({
      ...row,
      parts: row.parts.map(part => ({ ...part })),
    })),
  };
}
